import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { uploadPortfolioImageAsync, fileToBase64, compressImage } from '../utils/imageUpload';

interface PortfolioGalleryUploadProps {
  currentImages?: string[];
  onImagesChange: (images: string[]) => void;
  userId: string;
  disabled?: boolean;
  className?: string;
}

const PortfolioGalleryUpload: React.FC<PortfolioGalleryUploadProps> = ({
  currentImages = [],
  onImagesChange,
  userId,
  disabled = false,
  className = ''
}) => {
  const { t } = useTranslation();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    try {
      setIsUploading(true);
      const newImages = [...currentImages];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setUploadingIndex(i);

        // 이미지 압축
        const compressedFile = await compressImage(file, 800, 0.8);

        // Firebase Storage에 업로드 (기존 앱과 동일한 경로 사용)
        const uploadedUrl = await uploadPortfolioImageAsync(compressedFile, userId);
        newImages.push(uploadedUrl);
      }

      onImagesChange(newImages);
    } catch (error) {
      console.error('Portfolio image upload error:', error);
alert(t('cardEdit.errors.imageUploadFailed', 'Image upload failed'));
    } finally {
      setIsUploading(false);
      setUploadingIndex(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleGalleryClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveImage = (index: number) => {
    const newImages = currentImages.filter((_, i) => i !== index);
    onImagesChange(newImages);
  };

  return (
    <div className={`portfolio-gallery-upload ${className}`}>
      {/* 숨겨진 파일 입력 */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled || isUploading}
      />

      {/* 포트폴리오 이미지 미리보기 */}
      <div className="space-y-4">
        {/* 이미지 그리드 */}
        {currentImages.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {currentImages.map((imageUrl, index) => (
              <div key={`${index}-${imageUrl}`} className="relative group">
                <div className="aspect-[9/5] rounded-lg overflow-hidden border border-gray-200 bg-gray-100">
                  <img
                    src={imageUrl}
                    alt={`${t('functions.portfolio', 'Portfolio')} ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  
                  {/* 업로드 중 오버레이 */}
                  {isUploading && uploadingIndex === index && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                    </div>
                  )}
                </div>

                {/* 삭제 버튼 */}
                <button
                  onClick={() => handleRemoveImage(index)}
                  disabled={disabled || isUploading}
                  className="absolute -top-2 -right-2 bg-slate-500 text-white rounded-full p-1 hover:bg-slate-600 disabled:opacity-50 opacity-0 group-hover:opacity-100 transition-opacity"
                                     title={t('common.delete', 'Delete')}
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}

        {/* 업로드 버튼 */}
        <div className="flex justify-center">
          <button
            onClick={handleGalleryClick}
            disabled={disabled || isUploading}
            className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
            </svg>
            <span>
              {currentImages.length > 0 ? t('cardEdit.addFromGallery', 'Add More Images') : t('cardEdit.portfolioImages', 'Add Portfolio Images')}
            </span>
          </button>
        </div>

        {/* 업로드 상태 표시 */}
        {isUploading && (
          <div className="text-center text-sm text-gray-600">
                         {t('common.saving', 'Uploading...')} ({uploadingIndex !== null ? uploadingIndex + 1 : 0})
          </div>
        )}

        {/* 안내 메시지 */}
        {currentImages.length === 0 && !isUploading && (
          <div className="text-center text-sm text-gray-500">
            {t('cardEdit.addPortfolioImages', 'Please add images to display in your portfolio')}
          </div>
        )}
      </div>
    </div>
  );
};

export default PortfolioGalleryUpload;
